import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Briefcase, Globe, Zap, ArrowRight } from 'lucide-react';

const Northeastern: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Northeastern";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#d41b2c] selection:text-white">
            {/* Top Bar - Black */}
            <div className="bg-black text-white py-2 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:text-[#d41b2c]">Resources</a>
                <a href="#" className="hover:text-[#d41b2c]">Calendars</a>
                <a href="#" className="hover:text-[#d41b2c]">Maps</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 shadow-sm border-b border-gray-100">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-black text-white p-1 rounded-full">
                            <span className="font-serif font-black text-2xl w-8 h-8 flex items-center justify-center">{schoolName.charAt(0)}</span>
                        </div>
                        <span className="font-sans font-bold text-2xl text-black tracking-tight leading-none">{schoolName} University</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-black text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#d41b2c] transition-colors font-extrabold">Admissions</a>
                        <a href="#" className="hover:text-[#d41b2c] transition-colors font-extrabold">Academics</a>
                        <a href="#" className="hover:text-[#d41b2c] transition-colors font-extrabold">Research</a>
                        <a href="#" className="hover:text-[#d41b2c] transition-colors font-extrabold">Experience</a>
                        <a href="#" className="hover:text-[#d41b2c] transition-colors font-extrabold">Global</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-black hover:text-[#d41b2c]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-black" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Experience Powered */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1592671987588-e24c56255c42?q=80&w=2070&auto=format&fit=crop" 
                    alt="ISEC Building" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Red/Black Gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-3xl text-white">
                         <h1 className="text-5xl md:text-8xl font-black mb-8 leading-none italic transform -skew-x-3">
                             Experience <br/> <span className="text-[#d41b2c]">Powered.</span>
                         </h1>
                         <p className="text-xl md:text-2xl mb-12 font-bold max-w-xl">
                             We are a global research university with a distinctive, experiential approach to education.
                         </p>
                         <Button className="bg-[#d41b2c] hover:bg-[#b00b1c] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg transform -skew-x-3 border-2 border-transparent hover:border-white">
                             <span className="transform skew-x-3">Apply to {schoolName.split(' ')[0]}</span>
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Co-ops */}
            <section className="bg-[#f2f2f2] py-24 px-6 md:px-12 text-black">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div>
                         <Briefcase size={48} className="text-[#d41b2c] mx-auto mb-6" />
                         <h3 className="text-4xl font-black mb-2">#1</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">In Co-ops/Internships (US News)</p>
                     </div>
                      <div>
                         <Globe size={48} className="text-[#d41b2c] mx-auto mb-6" />
                         <h3 className="text-4xl font-black mb-2">13</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Global Campuses</p>
                     </div>
                      <div>
                         <Zap size={48} className="text-[#d41b2c] mx-auto mb-6" />
                         <h3 className="text-4xl font-black mb-2">R1</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Highest Research Activity</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-4 border-black pb-4">
                         <h2 className="text-4xl font-black text-black uppercase transform -skew-x-3 italic">News @ {schoolName.split(' ')[0]}</h2>
                         <a href="#" className="font-bold text-[#d41b2c] text-xs uppercase hover:underline mb-2">All Stories</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                         {[
                             "Reserachers develop AI to predict climate change impacts",
                             "New campus opens in Miami's Wynwood district",
                             "Northeastern student startup raises $10M seed round"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-64 mb-6 overflow-hidden relative shadow-lg">
                                     <div className="absolute top-0 left-0 bg-[#d41b2c] text-white font-black px-4 py-2 text-sm uppercase transform -skew-x-12 -ml-2 mt-4 z-10 shadow-md">Featured</div>
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+110}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="News"/>
                                 </div>
                                 <h3 className="text-2xl font-bold text-black group-hover:text-[#d41b2c] transition-colors leading-tight mb-4">{title}</h3>
                                  <div className="flex items-center gap-2 text-black font-bold uppercase text-xs tracking-widest group-hover:gap-3 transition-all">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-black text-white py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-black text-2xl mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="text-gray-400 mb-6">
                              360 Huntington Ave <br/>
                              Boston, MA 02115 <br/>
                              617.373.2000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest text-[#d41b2c]">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Emergency</a></li>
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Policies</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Careers</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Giving</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-800 pt-8">
                      © 2026 {schoolName} University
                  </div>
             </footer>
        </div>
    );
};

export default Northeastern;
